import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Eye, EyeOff, X, Loader2, TrendingUp } from "lucide-react";
import { PopUpModal, Toastify, Typography } from "top-brains-ui";
import { VALIDATION_CONSTANTS } from "@src/constants/UiConstants";
import bannerElp from "@src/assets/bannerElp.svg";
import calibrateLogo from "@src/assets/CalibrateLogo.svg";
import { LoginInitialValues } from "./types";
import styles from "./electronLogin.module.scss";

interface ElectronLoginProps {
  onSubmit: (values: LoginInitialValues, formikHelpers?: any) => Promise<void>;
  isLoading: boolean;
  onExit: () => void;
  showExpiryModal?: boolean;
}

const LearnIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Student Head */}
    <circle cx="12" cy="4.2" r="2.8" fill="#3F4DB8" />

    {/* Shoulder Curve */}
    <path
      d="M7.8 9.5C8.8 8.4 10.3 7.7 12 7.7C13.7 7.7 15.2 8.4 16.2 9.5"
      stroke="#3F4DB8"
      strokeWidth="1.8"
      strokeLinecap="round"
    />

    {/* Open Book - Left Page */}
    <path
      d="M11.2 11.5C9 10.5 6 10.8 4.2 11.8C3.8 12.1 3.5 12.5 3.5 13V18.2C3.5 18.8 4 19.3 4.6 19C6.4 18.2 9 17.9 11.2 18.8V11.5Z"
      fill="#3F4DB8"
    />

    {/* Open Book - Right Page */}
    <path
      d="M12.8 11.5C15 10.5 18 10.8 19.8 11.8C20.2 12.1 20.5 12.5 20.5 13V18.2C20.5 18.8 20 19.3 19.4 19C17.6 18.2 15 17.9 12.8 18.8V11.5Z"
      fill="#3F4DB8"
    />

    {/* Left Hand Holding Book */}
    <circle cx="2.5" cy="16" r="1.5" fill="#3F4DB8" />

    {/* Right Hand Holding Book */}
    <circle cx="21.5" cy="16" r="1.5" fill="#3F4DB8" />
  </svg>
);

const ExperienceIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_3020_8230)">
      <path
        d="M14.6008 6.43738L16.9409 4.73706C17.2039 4.5459 17.3137 4.20752 17.2134 3.89844C17.113 3.58936 16.8252 3.38025 16.5 3.38025H13.6069L12.7134 0.629274C12.613 0.320191 12.3252 0.111084 12 0.111084C11.6748 0.111084 11.387 0.320191 11.2866 0.629274L10.3931 3.38025H7.5C7.1748 3.38025 6.88696 3.58936 6.78662 3.89844C6.68628 4.20752 6.79614 4.5459 7.05908 4.73706L9.39917 6.43738L8.50561 9.18872C8.40527 9.49781 8.51514 9.83655 8.77807 10.0273C9.04174 10.2185 9.39624 10.2185 9.65991 10.0273L12 8.32703L14.3401 10.0273C14.6008 10.2163 14.9621 10.2157 15.2219 10.0273C15.4849 9.83655 15.5947 9.49781 15.4944 9.18872L14.6008 6.43738Z"
        fill="#3F4DB8"
      />
      <path
        d="M6.75 6.36304H5.54809C5.22216 6.36304 4.93506 6.15466 4.83471 5.84485L4.46337 4.70153C4.36303 4.39246 4.07519 4.18335 3.75 4.18335C3.4248 4.18335 3.13696 4.39246 3.03662 4.70153L2.66528 5.84485C2.56494 6.15466 2.27783 6.36304 1.9519 6.36304H0.749996C0.424804 6.36304 0.136961 6.57214 0.0366187 6.88123C-0.0637238 7.19031 0.0461437 7.52905 0.309079 7.71985L1.28174 8.4259C1.54541 8.6178 1.65454 8.95508 1.5542 9.26489C1.5542 9.26489 1.18413 10.4039 1.18287 10.4078C0.964661 11.0794 1.7643 11.6641 2.33716 11.2465L3.30909 10.54C3.57276 10.3481 3.92725 10.3481 4.19092 10.54C4.19092 10.54 5.16274 11.2464 5.16285 11.2465C5.72613 11.6559 6.54009 11.0946 6.31714 10.4078L5.9458 9.26526C5.84546 8.95508 5.95459 8.6178 6.21826 8.42627L7.19092 7.71985C7.45386 7.52906 7.56372 7.19032 7.46338 6.88123C7.36304 6.57215 7.07519 6.36304 6.75 6.36304Z"
        fill="#3F4DB8"
      />
      <path
        d="M18.0542 9.26489C18.0542 9.26489 17.6843 10.4035 17.6829 10.4078C17.4648 11.0789 18.2665 11.6625 18.8372 11.2465L19.8091 10.54C20.0728 10.3481 20.4273 10.3481 20.6909 10.54L21.6628 11.2465C22.2385 11.6662 23.0361 11.0822 22.8171 10.4078L22.4458 9.26526C22.3455 8.95508 22.4546 8.6178 22.7183 8.42627L23.6909 7.71985C23.9539 7.52906 24.0637 7.19032 23.9634 6.88123C23.863 6.57215 23.5752 6.36304 23.25 6.36304H22.0481C21.7222 6.36304 21.4351 6.15466 21.3347 5.84485L20.9634 4.70154C20.863 4.39246 20.5752 4.18335 20.25 4.18335C19.9248 4.18335 19.637 4.39246 19.5366 4.70154L19.1653 5.84485C19.0649 6.15466 18.7778 6.36304 18.4519 6.36304H17.25C16.9248 6.36304 16.637 6.57214 16.5366 6.88123C16.4363 7.19031 16.5461 7.52905 16.8091 7.71985L17.7817 8.4259C18.0454 8.61779 18.1545 8.95507 18.0542 9.26489Z"
        fill="#3F4DB8"
      />
      <path
        d="M13.125 12H10.875C9.63427 12 8.625 13.0093 8.625 14.25V21.75C8.625 22.9907 9.63427 24 10.875 24H13.125C14.3657 24 15.375 22.9907 15.375 21.75V14.25C15.375 13.0093 14.3657 12 13.125 12Z"
        fill="#3F4DB8"
      />
      <path
        d="M21.75 15.0002H19.5C18.2593 15.0002 17.25 16.0095 17.25 17.2502V21.7502C17.25 22.991 18.2593 24.0002 19.5 24.0002H21.75C22.9907 24.0002 24 22.991 24 21.7502V17.2502C24 16.0095 22.9907 15.0002 21.75 15.0002Z"
        fill="#3F4DB8"
      />
      <path
        d="M4.5 15.0002H2.25C1.00927 15.0002 0 16.0095 0 17.2502V21.7502C0 22.991 1.00927 24.0002 2.25 24.0002H4.5C5.74073 24.0002 6.75 22.991 6.75 21.7502V17.2502C6.75 16.0095 5.74073 15.0002 4.5 15.0002Z"
        fill="#3F4DB8"
      />
    </g>
    <defs>
      <clipPath id="clip0_3020_8230">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
    .test("dot-in-domain", "Invalid email", (value) => {
      if (!value) return false;
      const parts = value.split("@");
      return parts.length === 2 && parts[1].includes(".");
    })
    .required(VALIDATION_CONSTANTS.EMAIL_REQUIRED),
  password: Yup.string()
    .min(8, VALIDATION_CONSTANTS.PASSWORD_MIN_LENGTH)
    .required(VALIDATION_CONSTANTS.PASSWORD_REQUIRED),
});

const initialValues: LoginInitialValues = {
  email: "",
  password: "",
  rememberMe: false,
};

const ElectronLogin: React.FC<ElectronLoginProps> = ({
  onSubmit,
  isLoading,
  onExit,
  showExpiryModal = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  return (
    <>
      <div className={styles.electronLoginWrapper}>
        {/* Corner shapes with exact design angles and dimensions */}
        <svg
          className={styles.cornerTopLeft}
          width="118"
          height="118"
          viewBox="0 0 118 118"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 0 H118 A 203 203 0 0 1 0 118 Z" fill="#3f4db8" />
        </svg>

        <svg
          className={styles.cornerBottomLeft}
          width="125"
          height="125"
          viewBox="0 0 125 125"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 125 H125 A 215 215 0 0 0 0 0 Z" fill="#3f4db8" />
        </svg>

        {/* Close / Exit application button */}
        <button
          type="button"
          className={styles.closeButton}
          onClick={onExit}
          title="Exit Application"
          aria-label="Exit Application"
        >
          <X size={22} strokeWidth={2.4} />
        </button>

        {/* Left Section */}
        <div className={styles.leftSection}>
          <div className={styles.leftContent}>
            {/* Banner Illustration */}
            <img
              src={bannerElp}
              alt="Calibrate Learning Platform"
              className={styles.bannerImage}
            />

            {/* Main Headline */}
            <h1 className={styles.headline}>
              Learn deeply. Apply Practically. Grow
              <br />
              Continuously.
            </h1>

            {/* Subheading */}
            <p className={styles.subheadline}>
              Learn deeply, Experience it hands-on, Excel and grow.
            </p>

            {/* 3 Feature Cards */}
            <div className={styles.cardsContainer}>
              <div className={styles.card}>
                <div className={styles.iconWrapper}>
                  <LearnIcon />
                </div>
                <h3 className={styles.cardTitle}>Learn</h3>
                <p className={styles.cardDescription}>
                  Build a strong foundation through self-paced learning designed
                  around your schedule.
                </p>
              </div>

              <div className={styles.card}>
                <div className={styles.iconWrapper}>
                  <ExperienceIcon />
                </div>
                <h3 className={styles.cardTitle}>Experience</h3>
                <p className={styles.cardDescription}>
                  Gain real-world skills through practical, hands-on projects
                  and exercises.
                </p>
              </div>

              <div className={styles.card}>
                <div className={styles.iconWrapper}>
                  <TrendingUp size={24} color="#3f4db8" strokeWidth={2.4} />
                </div>
                <h3 className={styles.cardTitle}>Excel</h3>
                <p className={styles.cardDescription}>
                  Turn knowledge into performance and grow with confidence in
                  your career.
                </p>
              </div>
            </div>

            {/* What you'll gain */}
            <div className={styles.gainSection}>
              <div className={styles.gainTitleContainer}>
                <span className={styles.gainTitlePrefix}>WHAT YOU&apos;</span>
                <span className={styles.gainTitleUnderline}>LL GAIN</span>
              </div>
              <div className={styles.gainList}>
                <div className={`${styles.gainItem} ${styles.step1}`}>
                  <span className={styles.bulletDot}>•</span>
                  <span>In-depth understanding, built at your own pace.</span>
                </div>
                <div className={`${styles.gainItem} ${styles.step2}`}>
                  <span className={styles.bulletDot}>•</span>
                  <span>
                    Practical confidence to apply your skills in real scenarios.
                  </span>
                </div>
                <div className={`${styles.gainItem} ${styles.step3}`}>
                  <span className={styles.bulletDot}>•</span>
                  <span>
                    Measurable growth in how you learn, think and perform.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className={styles.dividerLine} />

        {/* Right Section */}
        <div className={styles.rightSection}>
          <div className={styles.rightContent}>
            {/* Calibrate Logo */}
            <img
              src={calibrateLogo}
              alt="Calibrate Logo"
              className={styles.calibrateLogo}
            />

            {/* Welcome Heading */}
            <h2 className={styles.welcomeTitle}>Welcome back!</h2>
            <p className={styles.welcomeSubtitle}>
              Sign in to continue your CALIBRATE learning journey
            </p>

            {/* Login Form */}
            <Formik
              initialValues={initialValues}
              validationSchema={loginValidationSchema}
              onSubmit={onSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur }) => {
                const showEmailLabel = emailFocused || Boolean(values.email);
                const showPasswordLabel =
                  passwordFocused || Boolean(values.password);

                return (
                  <Form className={styles.formContainer} autoComplete="off">
                    <div className={styles.fieldGroup}>
                      {showEmailLabel && (
                        <label
                          htmlFor="electron-login-email"
                          className={styles.fieldLabel}
                        >
                          Email
                        </label>
                      )}
                      <div className={styles.inputWrapper}>
                        <input
                          id="electron-login-email"
                          name="email"
                          type="email"
                          placeholder={showEmailLabel ? "" : "Enter your email"}
                          onChange={handleChange}
                          onFocus={() => setEmailFocused(true)}
                          onBlur={(e) => {
                            setEmailFocused(false);
                            handleBlur(e);
                          }}
                          value={values.email}
                          className={`${styles.inputField} ${
                            errors.email && touched.email
                              ? styles.inputError
                              : ""
                          }`}
                          autoComplete="off"
                        />
                      </div>
                      {errors.email && touched.email && (
                        <div className={styles.errorMsg}>{errors.email}</div>
                      )}
                    </div>

                    <div className={styles.fieldGroup}>
                      {showPasswordLabel && (
                        <label
                          htmlFor="electron-login-password"
                          className={styles.fieldLabel}
                        >
                          Password
                        </label>
                      )}
                      <div className={styles.inputWrapper}>
                        <input
                          id="electron-login-password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder={
                            showPasswordLabel ? "" : "Enter your password"
                          }
                          onChange={handleChange}
                          onFocus={() => setPasswordFocused(true)}
                          onBlur={(e) => {
                            setPasswordFocused(false);
                            handleBlur(e);
                          }}
                          value={values.password}
                          className={`${styles.inputField} ${
                            styles.hasPasswordToggle
                          } ${
                            errors.password && touched.password
                              ? styles.inputError
                              : ""
                          }`}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className={styles.togglePasswordBtn}
                          onClick={() => setShowPassword((prev) => !prev)}
                          title={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                      {errors.password && touched.password && (
                        <div className={styles.errorMsg}>{errors.password}</div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className={styles.signInButton}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 size={18} className={styles.spinner} />
                          <span>Signing In...</span>
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </button>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>

      <Toastify />

      {showExpiryModal && (
        <PopUpModal
          isOpen={showExpiryModal}
          onClose={() => {}}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          titleMessage="Application Expired"
          iconName="warning"
          subTitleMessage=""
          modalMessage={
            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <Typography as="p" fontSize={16}>
                Your application access has expired.
              </Typography>
              <Typography as="p" fontSize={16}>
                You cannot proceed further. Please exit the application.
              </Typography>
            </div>
          }
          onContinue={onExit}
          showCloseIcon={false}
          showFirstButton={false}
          secondButtonLabel="Exit"
        />
      )}
    </>
  );
};

export default ElectronLogin;
